import { Link as RouterLink } from "react-router-dom";
import { Link } from "@mui/material";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { TransactionActionMenu } from "./TransactionActionMenu";
import Tooltip from "@mui/material/Tooltip";
import ThrottledComponent from "src/common/components/ThrottledComponent";

export const transactionsListColumns = (
  isReadOnly,
  downloadInvoice,
  isGeneratingPDF = false,
  currentDownloadingId = null
) => ([
  {
    field: "transactionId",
    headerName: "Transaction ID",
    flex: 0.7,
    minWidth: 150,
    resizable: true,
    renderCell: ({ value, id }) => (
      <>
        {isReadOnly ? value : <Link underline="none" component={RouterLink} to={`/transactions/${id}`}>
          {value}
        </Link>}
      </>
    ),
  },
  {
    field: "email",
    headerName: "Email",
    flex: 0.5,
    minWidth: 250,
    resizable: true,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
  {
    field: "phone",
    headerName: "Phone no.",
    flex: 0.5,
    minWidth: 150,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
  {
    field: "chargepoint_id",
    headerName: "ChargeBox ID",
    flex: 0.7,
    minWidth: 120,
  },
  {
    field: "chargepoint_name",
    headerName: "Charger Name",
    flex: 0.7,
    minWidth: 120,
  },
  {
    field: "connectorId",
    headerName: "Connector ID",
    flex: 0.6,
    minWidth: 120,
  },
  {
    field: "payment_type",
    headerName: "Payment Type",
    flex: 0.5,
    minWidth: 130,
  },
  {
    field: "totalAmountPaid",
    headerName: "Amount Paid",
    flex: 0.6,
    minWidth: 130,
  },
  {
    field: "totalAmountCharged",
    headerName: "Charging Cost",
    flex: 0.6,
    minWidth: 140,
  },
  {
    field: "totalRefund",
    headerName: "Refund / Wallet balance",
    flex: 0.8,
    minWidth: 200,
  },
  {
    field: "totalKwConsumed",
    headerName: "Total Kw Consumed",
    flex: 0.8,
    minWidth: 200,
    renderCell: (params) => {
      const value =
        Math.abs(params.row?.stop_meter_value - params.row?.start_meter_value) / 1000;
      return `${value.toFixed(3)} kWh`;
    },
  },
  {
    field: "source",
    headerName: "Source",
    flex: 0.8,
    minWidth: 200,
  },
  {
    field: "starttime",
    headerName: "Start Date/Time",
    type: "date",
    flex: 0.7,
    minWidth: 200,
    renderCell: ({ value }) => (
      <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
        <span>{yearFirstDateTimeFormat(value)}</span>
      </Tooltip>
    ),
  },
  {
    field: "stoptime",
    headerName: "Stop Date/Time",
    type: "date",
    flex: 0.7,
    minWidth: 180,
    renderCell: ({ value }) => (
      <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
        <span>{yearFirstDateTimeFormat(value)}</span>
      </Tooltip>
    ),
  },
  {
    field: "status",
    headerName: "status",
    flex: 0.8,
    minWidth: 150,
  },
  {
    field: "notes",
    headerName: "notes",
    flex: 0.8,
    minWidth: 200,
  },
  {
    field: "Actions",
    flex: 0.5,
    sortable: false,
    minWidth: 80,
    renderCell: (props) => (
      <ThrottledComponent>
        <TransactionActionMenu
          {...props}
          downloadInvoice={downloadInvoice}
          isGeneratingPDF={isGeneratingPDF}
          currentDownloadingId={currentDownloadingId}
        />
      </ThrottledComponent>
    ),
  },
]);

export const chartLabelConstants = {
  "Current.Export": "Instantaneous current flow from EV",
  "Current.Import": "Instantaneous current flow to EV",
  "Current.Offered": "Maximum current offered to EV",
  "Energy.Active.Export.Register": "Energy exported by EV (Wh or kWh)",
  "Energy.Active.Import.Register": "Energy imported by EV (Wh or kWh)",
  "Energy.Reactive.Export.Register":
    "Reactive energy exported by EV (varh or kvarh)",
  "Energy.Reactive.Import.Register":
    "Reactive energy imported by EV (varh or kvarh)",
  "Energy.Active.Export.Interval": "Energy exported by EV (Wh or kWh)",
  "Energy.Active.Import.Interval": "Energy imported by EV (Wh or kWh)",
  "Energy.Reactive.Export.Interval":
    "Reactive energy exported by EV. (varh or kvarh)",
  "Energy.Reactive.Import.Interval":
    "Reactive energy imported by EV. (varh or kvarh)",
  Frequency: "Instantaneous reading of powerline frequency",
  "Power.Active.Export": "Instantaneous active power exported by EV. (W or kW)",
  "Power.Active.Import": "Instantaneous active power imported by EV. (W or kW)",
  "Power.Factor": "Instantaneous power factor of total energy flow",
  "Power.Offered": "Maximum power offered to EV",
  "Power.Reactive.Export":
    "Instantaneous reactive power exported by EV. (var or kvar)",
  "Power.Reactive.Import":
    "Instantaneous reactive power imported by EV. (var or kvar)",
  RPM: "Fan speed in RPM",
  Soc: "State of charge of charging vehicle in percentage",
  SoC: "State of charge of charging vehicle in percentage",
  Temperature: "Temperature reading inside Charge Point",
  Voltage: "Instantaneous AC RMS supply voltage",
};

export const chartTypeToShow = [
  "Temperature",
  "Voltage",
  "Current.Import",
  "Power.Active.Import",
  "SoC"
];


export const transactionsPayoutColumns = [
  {
    field: "transactionId",
    headerName: "Transaction Id",
    flex: 0.8,
    renderCell: ({ value }) => (
      <div style={{ width: '100%', textAlign: 'center' }}>{value}</div>
    ),
  },
  {
    field: "chargeboxId",
    headerName: "ChargeBox ID",
    flex: 1,
    renderCell: ({ value }) => (
      <div style={{ width: '100%', textAlign: 'center' }}>{value}</div>
    ),
  },
  {
    field: "connectorId",
    headerName: "Connector ID",
    flex: 0.8,
    renderCell: ({ value }) => (
      <div style={{ width: '100%', textAlign: 'center' }}>{value}</div>
    ),
  },
  {
    field: "starttime",
    headerName: "Start Date/Time",
    type: "date",
    flex: 1,
    renderCell: ({ value }) => (
      <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
        <span>{yearFirstDateTimeFormat(value)}</span>
      </Tooltip>
    ),
  },
  {
    field: "stoptime",
    headerName: "Stop Date/Time",
    type: "date",
    flex: 1,
    renderCell: ({ value }) => (
      <Tooltip title={yearFirstDateTimeFormat(value)} placement="top">
        <span>{yearFirstDateTimeFormat(value)}</span>
      </Tooltip>
    ),
  },
  {
    field: "payoutAmount",
    headerName: "Payout Amount",
    flex: 1,
    renderCell: ({ value }) => (
      <div style={{ width: '100%', textAlign: 'center' }}>{value}</div>
    ),
  },
  {
    field: "status",
    headerName: "status",
    flex: 1,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top">
        <span>{value}</span>
      </Tooltip>
    ),
  },
];



export const infoEmail = "apps@oazaenergy.com"
